/**
 * The wheel gesture that rests the Composer, as arithmetic with no DOM.
 *
 * Lifted in shape from t3code's `composerScrollGesture`, narrowed to the one
 * trigger this card ships: reading back through a thread. t3code accumulates
 * the absolute delta in either direction and leans on the scroll container to
 * say whether the gesture could move anything; Forge accumulates only upward
 * travel, so wheeling down toward the newest turn can never rest the Composer
 * on its way there.
 *
 * The state is mutated in place rather than returned. It lives in a ref for
 * the whole life of a thread, a wheel gesture is dozens of events a second,
 * and allocating a fresh object per event to keep a pure signature would buy
 * nothing a test can see.
 */

/** Upward travel that rests the Composer, in CSS pixels. */
export const COMPOSER_SCROLL_COLLAPSE_THRESHOLD_PX = 24;

/**
 * Longest pause that still counts as the same gesture.
 *
 * A trackpad flick delivers events a few milliseconds apart, so 120ms is
 * comfortably inside one gesture and comfortably outside two. Without the
 * window, a person nudging the wheel once a second while reading would collect
 * enough travel to rest the Composer out of nowhere.
 */
export const COMPOSER_SCROLL_GESTURE_WINDOW_MS = 120;

/** Pixels a `DOM_DELTA_LINE` wheel event is worth. Matches t3code. */
const WHEEL_LINE_HEIGHT_PX = 16;

export interface ComposerScrollCollapseState {
  accumulatedUpwardPx: number;
  lastEventAt: number;
  /** Set while the rest of the current gesture must not rest the Composer. */
  collapseSuppressed: boolean;
}

export function createComposerScrollCollapseState(): ComposerScrollCollapseState {
  return {
    accumulatedUpwardPx: 0,
    lastEventAt: Number.NEGATIVE_INFINITY,
    collapseSuppressed: false,
  };
}

export function resetComposerScrollCollapse(state: ComposerScrollCollapseState): void {
  state.accumulatedUpwardPx = 0;
  state.lastEventAt = Number.NEGATIVE_INFINITY;
  state.collapseSuppressed = false;
}

/**
 * Retire the rest of the gesture that is running now.
 *
 * A person who starts typing has answered the question the gesture was asking,
 * but a trackpad flick keeps delivering wheel events for a while after the
 * fingers leave. Without this, that tail rests the Composer again a frame after
 * the first keystroke expanded it. A suppression that lands between gestures
 * means nothing and is dropped, so this can be called on every keystroke.
 */
export function suppressComposerScrollCollapse(
  state: ComposerScrollCollapseState,
  now: number,
): void {
  if (now - state.lastEventAt <= COMPOSER_SCROLL_GESTURE_WINDOW_MS) {
    state.collapseSuppressed = true;
  }
}

/**
 * A wheel event's vertical travel in CSS pixels, sign preserved.
 *
 * Browsers report three units. Reading them all as pixels lets one threshold
 * mean the same thing on a trackpad, a notched mouse wheel and a page-scroll
 * key, instead of resting after one notch on hardware that reports lines.
 */
export function normalizeWheelDeltaY(
  event: Pick<WheelEvent, "deltaY" | "deltaMode">,
  viewportHeightPx: number,
): number {
  if (event.deltaMode === 1) return event.deltaY * WHEEL_LINE_HEIGHT_PX;
  if (event.deltaMode === 2) return event.deltaY * viewportHeightPx;
  return event.deltaY;
}

/**
 * Fold one wheel event into the gesture. Returns true on the event that should
 * rest the Composer, and only on that one: the accumulator is spent by firing,
 * so holding an upward scroll rests the Composer once rather than repeatedly.
 */
export function recordComposerScrollCollapseWheel(
  state: ComposerScrollCollapseState,
  input: { now: number; deltaY: number; eligible: boolean },
): boolean {
  const startsNewGesture = input.now - state.lastEventAt > COMPOSER_SCROLL_GESTURE_WINDOW_MS;
  state.lastEventAt = input.now;
  if (startsNewGesture) {
    state.accumulatedUpwardPx = 0;
    state.collapseSuppressed = false;
  }

  // A downward wheel drops the tally instead of being ignored. A gesture that
  // changes its mind is not the deliberate read backwards this is looking for,
  // and letting the travel survive the reversal would rest the Composer during
  // a wobble.
  if (state.collapseSuppressed || !input.eligible || input.deltaY > 0) {
    state.accumulatedUpwardPx = 0;
    return false;
  }
  // A sideways wheel carries no vertical travel, so it neither adds to the
  // gesture nor contradicts it. Dropping the tally here would make a diagonal
  // trackpad swipe, which is most of them, unable to rest the Composer at all.
  if (input.deltaY === 0) return false;

  state.accumulatedUpwardPx += -input.deltaY;
  if (state.accumulatedUpwardPx < COMPOSER_SCROLL_COLLAPSE_THRESHOLD_PX) {
    return false;
  }
  state.accumulatedUpwardPx = 0;
  return true;
}
